import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingCart, Trash2, ArrowLeft, Send } from "lucide-react";
import { formatCHF } from "@/lib/orderStatus";

export default function Panier() {
    const { items, updateQuantity, removeItem, clear, totals } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [notes, setNotes] = useState("");
    const [deliveryDate, setDeliveryDate] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (items.length === 0) return;
        setSubmitting(true);
        try {
            const payload = {
                items: items.map((it) => ({
                    code: it.code || "",
                    article: it.article,
                    prix_kg_ht: it.prix_kg_ht,
                    unite: it.unite || "kg",
                    quantity: Number(it.quantity),
                })),
                notes: notes.trim() || null,
                delivery_date: deliveryDate || null,
            };
            const { data } = await api.post("/orders", payload);
            clear();
            toast.success(`Commande envoyée — un e-mail de confirmation vient de vous être transmis.`);
            navigate(`/mes-commandes`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="cart-page">
            <div className="flex items-center gap-3">
                <ShoppingCart size={22} color="#D4BC7C" strokeWidth={1.5} />
                <p className="bc-label-small">Panier</p>
            </div>
            <h1 className="font-serif text-[#D4BC7C] text-4xl sm:text-5xl mt-4">Votre commande</h1>
            <div className="bc-hairline-gold my-6" />

            {items.length === 0 ? (
                <div className="bc-card text-center py-14" data-testid="cart-empty">
                    <p className="font-serif text-[#D4BC7C] text-2xl">Votre panier est vide</p>
                    <p className="text-sm text-[#A0A0A0] mt-2">
                        Ajoutez des articles depuis la liste des tarifs professionnels.
                    </p>
                    <Link to="/tarifs" className="bc-btn mt-8 inline-flex" data-testid="cart-back-tarifs">
                        <ArrowLeft size={14} /> Voir les tarifs
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-8">
                    {/* Items */}
                    <div className="space-y-3" data-testid="cart-items">
                        {items.map((it) => (
                            <div key={it.id} className="bc-card p-4" data-testid={`cart-item-${it.id}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] text-[#A0A0A0] tracking-widest uppercase">{it.code || "—"}</p>
                                        <p className="text-[#F2ECE0] mt-1">{it.article}</p>
                                        <p className="text-xs text-[#D4BC7C] mt-1">
                                            {it.prix_kg_ht != null
                                                ? `CHF ${Number(it.prix_kg_ht).toFixed(2)} / ${it.unite || "kg"}`
                                                : "Sur demande"}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <div className="flex items-center border" style={{ borderColor: "var(--bc-border-strong)" }}>
                                            <button
                                                onClick={() => updateQuantity(it.id, it.quantity - 0.5)}
                                                className="px-2 py-1 text-[#D4BC7C]"
                                                data-testid={`cart-minus-${it.id}`}
                                            >
                                                −
                                            </button>
                                            <input
                                                type="number"
                                                step="0.5"
                                                value={it.quantity}
                                                onChange={(e) => updateQuantity(it.id, e.target.value)}
                                                className="w-16 text-center bg-transparent text-[#F2ECE0] py-1"
                                                data-testid={`cart-qty-${it.id}`}
                                            />
                                            <button
                                                onClick={() => updateQuantity(it.id, it.quantity + 0.5)}
                                                className="px-2 py-1 text-[#D4BC7C]"
                                                data-testid={`cart-plus-${it.id}`}
                                            >
                                                +
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => removeItem(it.id)}
                                            className="text-[#A0A0A0] hover:text-[#d97070]"
                                            aria-label="Retirer"
                                            data-testid={`cart-remove-${it.id}`}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--bc-border)" }}>
                                    <span className="text-[#A0A0A0] uppercase tracking-widest">Sous-total</span>
                                    <span className="text-[#D4BC7C] font-medium" data-testid={`cart-subtotal-${it.id}`}>
                                        {it.prix_kg_ht != null
                                            ? formatCHF(Number(it.quantity) * Number(it.prix_kg_ht))
                                            : "Sur demande"}
                                    </span>
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={() => {
                                if (window.confirm("Vider entièrement le panier ?")) clear();
                            }}
                            className="text-xs uppercase tracking-widest text-[#A0A0A0] hover:text-[#d97070]"
                            data-testid="cart-clear"
                        >
                            Vider le panier
                        </button>
                    </div>

                    {/* Summary + submit */}
                    <div className="bc-card sticky top-24 h-fit" data-testid="cart-summary">
                        <p className="bc-label-small">Récapitulatif</p>
                        <div className="bc-hairline-gold mt-3 mb-5" />
                        <div className="flex justify-between text-sm">
                            <span className="text-[#A0A0A0]">Articles</span>
                            <span className="text-[#F2ECE0]">{totals.count}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                            <span className="text-[#A0A0A0]">Total HT</span>
                            <span className="text-[#D4BC7C] font-serif text-xl" data-testid="cart-total-ht">
                                {formatCHF(totals.total_ht)}
                            </span>
                        </div>
                        {totals.has_sur_demande && (
                            <p className="text-[10px] text-[#A0A0A0] mt-2">
                                Certains articles sont sur demande — leur prix vous sera communiqué lors du traitement.
                            </p>
                        )}

                        <div className="mt-6">
                            <label className="bc-label">Date de livraison souhaitée</label>
                            <input
                                type="date"
                                className="bc-input"
                                value={deliveryDate}
                                onChange={(e) => setDeliveryDate(e.target.value)}
                                min={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
                                data-testid="cart-delivery-date"
                            />
                            <p className="text-[10px] text-[#A0A0A0] mt-1">Délai minimum : 48h.</p>
                        </div>

                        <div className="mt-4">
                            <label className="bc-label">Notes (optionnel)</label>
                            <textarea
                                className="bc-input"
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Découpe spécifique, précisions..."
                                data-testid="cart-notes"
                            />
                        </div>

                        {user && (
                            <p className="text-[10px] text-[#A0A0A0] mt-4 leading-relaxed">
                                Commande passée par <span className="text-[#D4BC7C]">{user.raison_sociale || user.prenom}</span>. Confirmation par e-mail à {user.email}.
                            </p>
                        )}

                        <button
                            className="bc-btn w-full mt-6"
                            onClick={submit}
                            disabled={submitting || items.length === 0}
                            data-testid="cart-submit"
                        >
                            <Send size={14} /> {submitting ? "Envoi..." : "Passer la commande"}
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
